import {
  ASCII_CHARS_REGEX,
  EMAIL_AND_PHONE_COMBINED_REGEX,
  EMAIL_REGEX,
  EMPTY_SPACES_REGEX
} from 'constants/utils';

import { Tab, Tabs } from '@mui/material';
import { Breadcrumbs, LoadingSpinner, Stack, Text } from 'components';
import { LinkWithIndirectRouting } from 'components/linkWithConfirm';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  getLsUserRoleObject,
  getMobileInfoBannerClosed,
  setMobileInfoBannerClosed
} from 'modules/networkTools/localStorageTokens';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSites, getFavoriteZCodes } from 'store/clientProfile/clientProfileActions';
import {
  ClientDTO,
  getClient,
  getClientHistory,
  postCreateClient,
  putEditClient
} from 'store/clients/clientsActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  getOtherScreenerDetails,
  getOtherScreenerHistory,
  getScreenerDetails,
  getScreenerHistory,
  getScreenerQuestions,
  getScreeners
} from 'store/screeener/screenerActions';
import {
  MSG_ASCII_CHARACTERS,
  MSG_FIELD_LESS_1000_CHARS,
  MSG_FIELD_LESS_100_CHARS,
  MSG_FIELD_LESS_20_CHARS,
  MSG_FIELD_LESS_220_CHARS,
  MSG_FIELD_LESS_40_CHARS,
  MSG_WHITE_SPACE_ONLY
} from 'utils/errorMessages';
import { addressSchema } from 'utils/validations/validations';
import * as Yup from 'yup';
import {
  getGoals,
  getNewGoalDraft,
  getNewTodoDraft,
  getPresets,
  getTodos
} from 'store/roadmaps/roadmapsActions';
import clsx from 'clsx';
import { useResponsive } from 'utils/hooks/useMediaQuery';

import { setChangeStatusModal, CareGap } from '../../../store/clients/clientsSlice';
import { ProfileInfo } from './profileInfo';
import style from './clientForm.module.scss';

function useRouteMatch(patterns: readonly string[]) {
  const { pathname } = useLocation();

  let match;

  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i];
    if (pathname.includes(pattern)) {
      match = pattern;
    }
  }

  return match;
}

export type FormValues = ClientDTO;

export const validationSchema = Yup.object({
  firstName: Yup.string()
    .max(40, MSG_FIELD_LESS_40_CHARS)
    .matches(EMPTY_SPACES_REGEX, MSG_WHITE_SPACE_ONLY)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .required('First name is required')
    .nullable(),
  lastName: Yup.string()
    .nullable()
    .max(40, MSG_FIELD_LESS_40_CHARS)
    .matches(EMPTY_SPACES_REGEX, MSG_WHITE_SPACE_ONLY)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .required('Last name is required'),
  preferredName: Yup.string()
    .nullable()
    .max(40, MSG_FIELD_LESS_40_CHARS)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  dob: Yup.string()
    .nullable()
    .required('DOB is required.')
    .test(
      'dob',
      'You can only add a date in the past',
      async (fieldVal): Promise<boolean | Yup.ValidationError | any> => {
        const today = dayjs();
        const selectedDate = dayjs(fieldVal);
        const isStartDateInPast = selectedDate.isAfter(today, 'd');

        return !isStartDateInPast;
      }
    ),
  clientIdentifier: Yup.string()
    .required('MRN or Customer ID is required.')
    .nullable()
    .max(40, MSG_FIELD_LESS_40_CHARS)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  last4Ssn: Yup.string()
    .nullable()
    .matches(/^[0-9]+$/, 'Please use numbers only.')
    .min(4, 'Must be 4 digits.')
    .max(4, 'Must be 4 digits.'),
  language: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(100, MSG_FIELD_LESS_100_CHARS),
  genderIdentity: Yup.string().nullable(),
  races: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string()
          .nullable()
          .max(100, MSG_FIELD_LESS_100_CHARS)
          .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
        name: Yup.string()
          .nullable()
          // .max(100, MSG_FIELD_LESS_100_CHARS)
          .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
      })
    )
    .nullable(),
  selfDescribed: Yup.string()
    .nullable()
    .when('races', {
      is: (races: string[]) => races && races.includes('SELF_DESCRIBED'),
      then: (schema) =>
        schema.matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS).max(220, MSG_FIELD_LESS_220_CHARS)
    })
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(220, MSG_FIELD_LESS_220_CHARS),
  genderSelfDescribed: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(100, MSG_FIELD_LESS_100_CHARS),
  pronouns: Yup.string().nullable(),
  pronounsSelfDescribed: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(100, MSG_FIELD_LESS_100_CHARS),
  insuranceId: Yup.string().nullable(),
  insurancePlan: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(100, MSG_FIELD_LESS_100_CHARS),
  veteranStatus: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(100, MSG_FIELD_LESS_100_CHARS),
  primaryPhoneNumber: Yup.string()
    .nullable()
    .required('Primary Phone Number is required.')
    .matches(EMAIL_AND_PHONE_COMBINED_REGEX, 'Primary Phone Number is not valid.'),
  secondaryPhoneNumber: Yup.string()
    .nullable()
    .matches(EMAIL_AND_PHONE_COMBINED_REGEX, 'Secondary Phone Number is not valid.'),
  email: Yup.string()
    .nullable()
    .matches(EMAIL_REGEX, 'Email is not valid.')
    .max(100, MSG_FIELD_LESS_100_CHARS),

  primaryAddress: addressSchema,
  sameAsPrimaryAddress: Yup.boolean().default(false),
  currentLocation: Yup.mixed().when('sameAsPrimaryAddress', {
    is: false,
    then: addressSchema,
    otherwise: Yup.mixed().notRequired()
  }),
  emergencyContacts: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .nullable()
        .max(100, MSG_FIELD_LESS_100_CHARS)
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
      phoneNumber: Yup.string()
        .nullable()
        .matches(EMAIL_AND_PHONE_COMBINED_REGEX, 'Phone Number is not valid.'),
      relationship: Yup.string().nullable(),
      relationshipOther: Yup.string()
        .nullable()
        .max(40, MSG_FIELD_LESS_40_CHARS)
        .matches(EMPTY_SPACES_REGEX, MSG_WHITE_SPACE_ONLY)
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    })
  ),
  insurances: Yup.array().of(
    Yup.object().shape({
      insuranceCategoryId: Yup.string().nullable(),
      insuranceCategoryName: Yup.string().nullable(),
      insurancePlan: Yup.string()
        .nullable()
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
        .max(100, MSG_FIELD_LESS_100_CHARS),
      insuranceNumber: Yup.string()
        .nullable()
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
        .max(20, MSG_FIELD_LESS_20_CHARS),
      endDate: Yup.string()
        .nullable()
        .test(
          'endDate',
          'End Date must be on or after Start Date.',
          async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
            const formikValues = values.parent;

            if (fieldVal == null || fieldVal === '') {
              return true;
            }

            console.log(formikValues.startDate);

            if (formikValues.startDate == null || formikValues.startDate === '') {
              return true;
            }
            //check if end time after start time
            let isStartTimeBeforeEndTime = true;
            const startTime = dayjs(`${formikValues.startDate}`);

            const endTime = dayjs(`${formikValues.endDate}`);

            isStartTimeBeforeEndTime = startTime.isSameOrBefore(endTime);

            return isStartTimeBeforeEndTime;
          }
        )
    })
  ),
  supportPeople: Yup.string().nullable().matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  careTeam: Yup.string().nullable().matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  referralClient: Yup.boolean()
    .typeError('Referral Client is required.')
    .required('Referral Client is required.'),
  referralDate: Yup.string()
    .nullable()
    .when('referralClient', {
      is: true,
      then: Yup.string().nullable().required('Referral Date is required when client is referred.')
    })
    .test('referralDate', 'Referral Date cannot be set in future.', (referralDate) => {
      const now = dayjs();
      return !dayjs(referralDate).isAfter(now, 'day');
    }),
  referredBy: Yup.string()
    .nullable()
    .max(100, MSG_FIELD_LESS_100_CHARS)
    .matches(EMPTY_SPACES_REGEX, MSG_WHITE_SPACE_ONLY)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  referrerPhone: Yup.string()
    .nullable()
    .matches(EMAIL_AND_PHONE_COMBINED_REGEX, 'Phone number format is not valid.'),
  referrerEmail: Yup.string()
    .nullable()
    .matches(EMAIL_REGEX, 'Email is not valid.')
    .max(100, MSG_FIELD_LESS_100_CHARS),
  // .when('referralClient', {
  //   is: true,
  //   then: Yup.string().nullable().required('Referring Provider Email is required.')
  // }),
  referrerNotes: Yup.string()
    .nullable()
    .max(1000, MSG_FIELD_LESS_1000_CHARS)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
});

const initialValues: FormValues = {
  careGaps: [] as CareGap[],
  firstName: null,
  lastName: null,
  preferredName: null,
  dob: null,
  clientIdentifier: null,
  last4Ssn: null,
  language: null,
  genderIdentity: null,
  genderSelfDescribed: null,
  pronouns: null,
  pronounsSelfDescribed: null,
  insuranceId: null,
  insurancePlan: null,
  veteranStatus: null,
  races: null,
  selfDescribed: null,
  primaryPhoneNumber: null,
  secondaryPhoneNumber: null,
  email: null,
  primaryAddress: {
    streetAddress: null,
    streetAddress2: null,
    city: null,
    state: null,
    zipCode: null,
    building: null,
    floor: null,
    room: null
  },
  sameAsPrimaryAddress: false,
  currentLocation: {
    streetAddress: null,
    streetAddress2: null,
    city: null,
    state: null,
    zipCode: null,
    building: null,
    floor: null,
    room: null
  },
  emergencyContacts: [
    { name: null, phoneNumber: null, relationship: null, relationshipOther: null }
  ],
  insurances: [
    {
      insuranceCategoryId: null,
      insuranceCategoryName: null,
      insurancePlan: null,
      insuranceNumber: null
    }
  ],
  supportPeople: null,
  careTeam: null,
  referralClient: null,
  referralDate: null,
  referredBy: null,
  referrerPhone: null,
  referrerEmail: null,
  referrerNotes: null,
  duplicatedClient: null
};

const useMobileInfoBanner = () => {
  const [isMobileInfoBannerClosed, setIsMobileInfoBannerClosed] = useState(
    getMobileInfoBannerClosed()
  );

  const handleCloseMobileInfoBanner = (value: boolean) => {
    setIsMobileInfoBannerClosed(value);
    setMobileInfoBannerClosed(value);
  };

  return { isMobileInfoBannerClosed, setIsMobileInfoBannerClosed: handleCloseMobileInfoBanner };
};

export const ClientFormWrapper = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const { clientId = '' } = useParams();
  const isEdit = Boolean(clientId);
  const navigate = useNavigate();
  const { data } = useAppSelector((state) => state.clients.client);

  const onSubmit = async (values: FormValues) => {
    const client: FormValues = {
      ...values,
      currentLocation: values.sameAsPrimaryAddress ? values.primaryAddress : values.currentLocation
    };
    if (clientId) {
      await dispatch(putEditClient({ clientId, client }));
    } else {
      const response = await dispatch(
        postCreateClient({
          client
        })
      );
      if (response.meta.requestStatus === 'fulfilled') {
        dispatch(setChangeStatusModal('changeStatus'));
        navigate(`/clients/edit/${response.payload.id}/profile`);
      }
    }
  };

  const normalizedInitialValues = (NewData?: FormValues | null): FormValues => ({
    ...initialValues,
    ...(NewData || {}),
    careGaps: NewData?.careGaps ?? initialValues.careGaps,
    emergencyContacts:
      NewData?.emergencyContacts && NewData?.emergencyContacts?.length > 0
        ? NewData.emergencyContacts
        : initialValues.emergencyContacts,
    insurances:
      NewData?.insurances && NewData?.insurances?.length > 0
        ? NewData.insurances
        : initialValues.insurances
  });

  return (
    // @ts-expect-error formik types are incorrect when you pass component={null}
    <Formik
      // @ts-expect-error formik types are incorrect when you pass component={null}
      enableReinitialize
      // @ts-expect-error formik types are incorrect when you pass component={null}
      validateOnMount
      // @ts-expect-error formik types are incorrect when you pass component={null}
      validationSchema={validationSchema}
      // @ts-expect-error formik types are incorrect when you pass component={null}
      component={null}
      // @ts-expect-error formik types are incorrect when you pass component={null}
      initialValues={isEdit ? normalizedInitialValues(data) : initialValues}
      // @ts-expect-error formik types are incorrect when you pass component={null}
      onSubmit={onSubmit}
    >
      {children}
    </Formik>
  );
};

const ClientForm = () => {
  const { isMobile } = useResponsive();
  const { isMobileInfoBannerClosed, setIsMobileInfoBannerClosed } = useMobileInfoBanner();
  const [loading, setLoading] = useState(false);
  const { clientId = '' } = useParams();
  const { data } = useAppSelector((state) => state.clients.client);
  const currentTab = useRouteMatch(['/profile', '/ourWork']);
  const dispatch = useAppDispatch();
  const { organizationId } = getLsUserRoleObject();
  const isEdit = Boolean(clientId);
  const { enableSites, aiScribe: isAiScribeEnabled } = useFlags();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        dispatch(getScreenerDetails({ clientId, type: 'impact' })),
        dispatch(getScreenerHistory({ clientId, type: 'impact' })),
        dispatch(getScreeners({ clientId })),
        dispatch(getScreenerQuestions({ clientId, type: 'impact' })),
        dispatch(getOtherScreenerDetails({ clientId })),
        dispatch(getOtherScreenerHistory({ clientId })),
        dispatch(getClient({ organizationId, clientId })),
        dispatch(getClientHistory({ clientId })),
        dispatch(getTodos({ clientId })),
        dispatch(getGoals({ clientId })),
        dispatch(getNewTodoDraft({ clientId, creationContext: 'ROADMAPS' })),
        dispatch(getNewGoalDraft({ clientId })),
        dispatch(getPresets({ clientId }))
      ]);
      setLoading(false);
    };
    if (clientId && clientId !== '') {
      fetchData();
    }
  }, [clientId]);

  useEffect(() => {
    if (enableSites && clientId) {
      dispatch(getSites({ clientId }));
    }
  }, [enableSites, clientId]);

  useEffect(() => {
    dispatch(getFavoriteZCodes());
  }, []);

  if (loading) {
    return (
      <Stack width="100%" display="flex" flexDirection="row" justifyContent="center" mt="120px">
        <LoadingSpinner />
      </Stack>
    );
  }

  return (
    <Stack>
      {!isMobileInfoBannerClosed && isMobile && isAiScribeEnabled && (
        <div className={style.mobileInfoBanner}>
          <Text weight="regular" size="large" className={style.mobileInfoText}>
            Having trouble seeing the app in mobile? Try turning your phone to landscape.
          </Text>
          <button onClick={() => setIsMobileInfoBannerClosed(true)}>
            <span className={clsx('material-icons', style.mobileInfoIcon)}>close</span>
          </button>
        </div>
      )}
      <div>
        <Breadcrumbs
          breadcrumbItems={[{ name: 'Clients', path: '/clients' }]}
          breadcrumbLastItem={isEdit ? `${data?.firstName} ${data?.lastName}` : 'New Client'}
          withBackground
        />
      </div>
      <Stack display="flex" flexDirection="row" gap="12px">
        <ClientFormWrapper>
          <Stack display="flex" flexDirection="row" gap="12px" width="100%">
            <Stack
              flex={3}
              position="relative"
              gap="12px"
              display="flex"
              alignItems="center"
              flexDirection="column"
            >
              <ProfileInfo />
            </Stack>
            <Stack flex={9}>
              <Tabs
                data-test-id="profile-tabs"
                sx={{
                  backgroundColor: 'white',
                  borderBottom: '2px solid #F1F5F9',
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#188AA1',
                    height: '3px'
                  },
                  '& a[aria-selected="true"]': {
                    color: '#188AA1',
                    backgroundColor: '#F8FAFC',
                    textTransform: 'none',
                    fontFamily: '"ProximaNova", sans-serif',
                    fontWeight: 600,
                    padding: '20px 0px',
                    pointerEvents: 'all'
                  },
                  '& a': {
                    height: '40px',
                    color: '#8DA0B9',
                    backgroundColor: '#fff',
                    textTransform: 'none',
                    fontFamily: '"ProximaNova", sans-serif',
                    fontWeight: 600,
                    padding: '20px 0px',
                    pointerEvents: 'all'
                  }
                }}
                value={currentTab}
                aria-label="nav tabs example"
              >
                {clientId && (
                  <Tab
                    content="Our Work"
                    value="/ourWork"
                    path={`/clients/edit/${clientId}/ourWork`}
                    component={LinkWithIndirectRouting}
                  />
                )}
                <Tab
                  content="Profile"
                  value="/profile"
                  path={clientId ? `/clients/edit/${clientId}/profile` : '/clients/new/profile'}
                  component={LinkWithIndirectRouting}
                />
              </Tabs>
              <div>
                <Outlet />
              </div>
            </Stack>
          </Stack>
        </ClientFormWrapper>
      </Stack>
    </Stack>
  );
};

export default ClientForm;
